import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { P } from './auth/permissions'
import { AppShell } from './components/AppShell'
import { Loading } from './components/ui'
import { ChangePasswordPage, LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { CashBookPage } from './pages/CashBookPage'
import { BankReconciliationPage } from './pages/BankReconciliationPage'
import { ImportPage } from './pages/ImportPage'
import { ChartOfAccountsPage } from './pages/ChartOfAccountsPage'
import { CashLocationsPage } from './pages/CashLocationsPage'
import { DepartmentsPage } from './pages/DepartmentsPage'
import { ExchangeRatesPage } from './pages/ExchangeRatesPage'
import { PartiesPage } from './pages/PartiesPage'
import { JournalPage } from './pages/JournalPage'
import { JournalEntryPage } from './pages/JournalEntryPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { PeriodsPage } from './pages/PeriodsPage'
import { ProfitAndLossPage } from './pages/reports/ProfitAndLossPage'
import { ClientFundsPage } from './pages/reports/ClientFundsPage'
import { TrialBalancePage } from './pages/reports/TrialBalancePage'
import { GaAnalysisPage } from './pages/reports/GaAnalysisPage'
import { AgingPage } from './pages/reports/AgingPage'
import { AccountStatementPage } from './pages/reports/AccountStatementPage'
import { EmployeeSchedulePage } from './pages/reports/EmployeeSchedulePage'
import { BalanceSheetPage } from './pages/reports/BalanceSheetPage'
import { CashFlowPage } from './pages/reports/CashFlowPage'
import { TaxSummaryPage } from './pages/reports/TaxSummaryPage'
import { UsersPage } from './pages/admin/UsersPage'
import { RolesPage } from './pages/admin/RolesPage'
import { SettingsPage } from './pages/admin/SettingsPage'
import { AuditLogPage } from './pages/admin/AuditLogPage'
import type { ReactNode } from 'react'

/**
 * Hides a route the user has no permission for. This is a courtesy, not a control — every endpoint
 * behind it checks the same permission server-side (ARCHITECTURE §4).
 */
function Requires({ permission, children }: { permission: string; children: ReactNode }) {
  const { can } = useAuth()

  if (!can(permission)) {
    return (
      <div className="banner warn">
        You do not have permission to view this. Ask an administrator if you think you should.
      </div>
    )
  }

  return <>{children}</>
}

export default function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return <Loading what="Restoring your session" />
  }

  if (!user) {
    return <LoginPage />
  }

  // A bootstrap password is known to whoever created the account, so nothing else opens until it
  // has been replaced.
  if (user.mustChangePassword) {
    return <ChangePasswordPage />
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<DashboardPage />} />

        <Route
          path="/books"
          element={
            <Requires permission={P.cashBook.view}>
              <CashBookPage />
            </Requires>
          }
        />
        <Route
          path="/journal"
          element={
            <Requires permission={P.journal.view}>
              <JournalPage />
            </Requires>
          }
        />
        <Route
          path="/journal/:id"
          element={
            <Requires permission={P.journal.view}>
              <JournalEntryPage />
            </Requires>
          }
        />
        <Route
          path="/reconciliation"
          element={
            <Requires permission={P.cashBook.view}>
              <BankReconciliationPage />
            </Requires>
          }
        />
        <Route
          path="/documents"
          element={
            <Requires permission={P.documents.view}>
              <DocumentsPage />
            </Requires>
          }
        />

        <Route
          path="/accounts"
          element={
            <Requires permission={P.accounts.view}>
              <ChartOfAccountsPage />
            </Requires>
          }
        />
        <Route
          path="/cash-locations"
          element={
            <Requires permission={P.cashBook.view}>
              <CashLocationsPage />
            </Requires>
          }
        />
        <Route
          path="/departments"
          element={
            <Requires permission={P.accounts.view}>
              <DepartmentsPage />
            </Requires>
          }
        />
        <Route
          path="/exchange-rates"
          element={
            <Requires permission={P.currencies.view}>
              <ExchangeRatesPage />
            </Requires>
          }
        />

        {/* No <Requires>: which permission applies depends on what is being imported, and the
            page itself says so per kind. Importing accounts is creating accounts; importing
            opening balances is posting an entry. */}
        <Route path="/import" element={<ImportPage />} />
        <Route
          path="/parties"
          element={
            <Requires permission={P.parties.view}>
              <PartiesPage />
            </Requires>
          }
        />

        <Route
          path="/reports/profit-and-loss"
          element={
            <Requires permission={P.reports.view}>
              <ProfitAndLossPage />
            </Requires>
          }
        />
        <Route
          path="/reports/client-funds"
          element={
            <Requires permission={P.reports.clientFunds}>
              <ClientFundsPage />
            </Requires>
          }
        />
        <Route
          path="/reports/trial-balance"
          element={
            <Requires permission={P.reports.view}>
              <TrialBalancePage />
            </Requires>
          }
        />
        <Route
          path="/reports/balance-sheet"
          element={
            <Requires permission={P.reports.view}>
              <BalanceSheetPage />
            </Requires>
          }
        />
        <Route
          path="/reports/cash-flow"
          element={
            <Requires permission={P.reports.view}>
              <CashFlowPage />
            </Requires>
          }
        />
        <Route
          path="/reports/tax-summary"
          element={
            <Requires permission={P.reports.view}>
              <TaxSummaryPage />
            </Requires>
          }
        />
        <Route
          path="/reports/ga-analysis"
          element={
            <Requires permission={P.reports.view}>
              <GaAnalysisPage />
            </Requires>
          }
        />
        <Route
          path="/reports/aging"
          element={
            <Requires permission={P.reports.view}>
              <AgingPage />
            </Requires>
          }
        />
        <Route
          path="/reports/account-statement"
          element={
            <Requires permission={P.reports.view}>
              <AccountStatementPage />
            </Requires>
          }
        />
        <Route
          path="/reports/employee-schedule"
          element={
            <Requires permission={P.reports.view}>
              <EmployeeSchedulePage />
            </Requires>
          }
        />

        <Route
          path="/periods"
          element={
            <Requires permission={P.periods.view}>
              <PeriodsPage />
            </Requires>
          }
        />

        <Route
          path="/admin/users"
          element={
            <Requires permission={P.admin.users}>
              <UsersPage />
            </Requires>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <Requires permission={P.admin.roles}>
              <RolesPage />
            </Requires>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <Requires permission={P.admin.settings}>
              <SettingsPage />
            </Requires>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Requires permission={P.admin.auditLog}>
              <AuditLogPage />
            </Requires>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}
